import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { CollapseButton } from '@ui/buttons/CollapseButton';
import { DATA_ANALYST_SOURCES } from '@constants/dataAnalystAgentSources';
import { FormStateContext } from '@/contexts/FormStateContext';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 12px 16px;
`;
const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;
const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding-top: 8px;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Row = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`;
const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin: 0;
`;

const toggleId = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

export const SourceFilterSection = () => {
  const { state, setFormStateValue } = React.useContext(FormStateContext);
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  const onlySources = state.onlySources || [];
  const excludeSources = state.excludeSources || [];
  const disabled = !!state.noRetrieval;

  const handleOnlyToggle = (id) => setFormStateValue('onlySources', toggleId(onlySources, id));
  const handleExcludeToggle = (id) => setFormStateValue('excludeSources', toggleId(excludeSources, id));

  return (
    <Wrapper>
      <HeaderRow onClick={() => setIsCollapsed((c) => !c)}>
        <Body5 bold>Data Sources</Body5>
        <div onClick={(e) => e.stopPropagation()}>
          <CollapseButton isCollapsed={isCollapsed} setCollapsed={setIsCollapsed} />
        </div>
      </HeaderRow>
      {!isCollapsed ? (
        <>
          {disabled ? (
            <Body7 isLowContrast>Disabled while retrieval is off.</Body7>
          ) : (
            <Body7 isLowContrast>
              Allow-list applies first; exclude-list removes from the remainder. Empty = no filter.
            </Body7>
          )}
          <Columns $disabled={disabled}>
            <Column>
              <Body5 bold>Include only</Body5>
              {DATA_ANALYST_SOURCES.map((s) => (
                <Row key={s.id} $disabled={disabled}>
                  <Checkbox
                    disabled={disabled}
                    checked={onlySources.includes(s.id)}
                    onChange={() => handleOnlyToggle(s.id)}
                  />
                  <Body5>{s.label}</Body5>
                </Row>
              ))}
            </Column>
            <Column>
              <Body5 bold>Exclude</Body5>
              {DATA_ANALYST_SOURCES.map((s) => (
                <Row key={s.id} $disabled={disabled}>
                  <Checkbox
                    disabled={disabled}
                    checked={excludeSources.includes(s.id)}
                    onChange={() => handleExcludeToggle(s.id)}
                  />
                  <Body5>{s.label}</Body5>
                </Row>
              ))}
            </Column>
          </Columns>
        </>
      ) : null}
    </Wrapper>
  );
};
